import { getTokens } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';
import type { NormalizedAddress } from './emergencyCalls.types';

const EMPTY_ADDRESS: NormalizedAddress = {
  buildingNumber: '',
  streetName: '',
  city: '',
  region: '',
  state: '',
  country: '',
  postalCode: '',
};

const MULTIPLE_ADDRESSES = /\s*ADRESSES?\s+MULTIPLES?\s*$/i;
const POSTAL = /[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d/;
const CIVIC_NUMBER = /^(\d+[A-Za-z]?(?:-\d+[A-Za-z]?)?)\s+(.+)$/;

const PARTICLES = new Set([
  'de',
  'du',
  'des',
  'le',
  'la',
  'les',
  'au',
  'aux',
  'et',
  'sur',
]);

const ROMAN = new Set([
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
  'XIII',
  'XIV',
  'XV',
  'XVI',
  'XVII',
  'XVIII',
  'XIX',
  'XX',
]);

/** Suffixes that only show up on French civic lines. */
const FRENCH_STREET_TYPES: Record<string, string> = {
  AL: 'Allée',
  BD: 'Boulevard',
  BOUL: 'Boulevard',
  CH: 'Chemin',
  CR: 'Croissant',
  CS: 'Côte',
  IM: 'Impasse',
  MO: 'Montée',
  RG: 'Rang',
  RL: 'Ruelle',
  RTE: 'Route',
  RU: 'Rue',
  SN: 'Sentier',
  TS: 'Terrasse',
  VO: 'Voie',
};

/** Also used in English. Moved in front only for a Quebec line. */
const QUEBEC_STREET_TYPES: Record<string, string> = {
  ...FRENCH_STREET_TYPES,
  AV: 'Avenue',
  BLVD: 'Boulevard',
  PL: 'Place',
};

const PROVINCE_CODES: Record<string, string> = {
  AB: 'AB',
  ALTA: 'AB',
  BC: 'BC',
  MB: 'MB',
  NB: 'NB',
  NL: 'NL',
  NS: 'NS',
  NT: 'NT',
  NU: 'NU',
  ON: 'ON',
  ONT: 'ON',
  PE: 'PE',
  PEI: 'PE',
  QC: 'QC',
  QUE: 'QC',
  SK: 'SK',
  YT: 'YT',
};

const CA_PROVINCES = new Set([
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
]);

const US_STATES = new Set([
  'AK',
  'AL',
  'AR',
  'AZ',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'IA',
  'ID',
  'IL',
  'IN',
  'KS',
  'KY',
  'LA',
  'MA',
  'MD',
  'ME',
  'MI',
  'MN',
  'MO',
  'MS',
  'MT',
  'NC',
  'ND',
  'NE',
  'NH',
  'NJ',
  'NM',
  'NV',
  'NY',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VA',
  'VT',
  'WA',
  'WI',
  'WV',
  'WY',
]);

type AddressObject = {
  home?: string;
  street?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country?: string;
};

type CivicLocation = {
  address_single_line?: string | null;
  city?: string | null;
  /**
   * Reverse-geocode parts for a geodetic ELS, CIRCUS, or ANI/ALI
   * location that arrived without a civic line.
   */
  addressObject?: AddressObject | null;
};

/**
 * Split a Hermes location into address parts.
 *
 * Civic ANI/ALI uses `address_single_line`. A broken Quebec CAD line
 * is repaired first; any other civic line is split as written.
 * Geodetic ELS, CIRCUS, and ANI/ALI use `addressObject` once reverse
 * geocode has filled it. The fields are the same either way.
 */
export function buildNormalizedAddress(
  location?: CivicLocation | null,
): NormalizedAddress {
  const raw =
    typeof location?.address_single_line === 'string'
      ? location.address_single_line.trim().replace(/\s+/g, ' ')
      : '';
  if (raw) {
    const recalled = recallNormalizedAddress(raw);
    if (recalled) return recalled;
    const stripped = raw.replace(MULTIPLE_ADDRESSES, '').trim();
    const repaired = repairBrokenCadLine(stripped);
    return parseAddress(repaired ?? stripped, location?.city);
  }
  if (location?.addressObject) {
    return fromAddressObject(location.addressObject, location.city);
  }
  return { ...EMPTY_ADDRESS, city: clean(location?.city) };
}

function fromAddressObject(
  addressObject: AddressObject,
  fallbackCity?: string | null,
): NormalizedAddress {
  const home = clean(addressObject.home);
  const street = clean(addressObject.street);
  const civic = home.match(CIVIC_NUMBER);
  const homeIsNumber = /^\d+[A-Za-z]?$/.test(home);
  let buildingNumber = '';
  let streetName = '';
  if (street) {
    buildingNumber = homeIsNumber ? home : civic?.[1] || '';
    streetName = street;
  } else if (civic) {
    buildingNumber = civic[1];
    streetName = civic[2];
  } else {
    streetName = home;
  }
  const region = clean(addressObject.region);
  return {
    buildingNumber,
    streetName,
    city: clean(addressObject.city) || clean(fallbackCity),
    region,
    state: region,
    country: normalizeCountry(clean(addressObject.country)),
    postalCode: clean(addressObject.postal_code),
  };
}

function normalizeCountry(value: string): string {
  const key = value.toLowerCase();
  if (key === 'us' || key === 'usa' || key === 'united states') return 'US';
  if (key === 'ca' || key === 'can' || key === 'canada') return 'CA';
  return value;
}

export function formatNormalizedAddress(
  address?: NormalizedAddress | null,
): string {
  if (!address) return '';
  const street = [address.buildingNumber, address.streetName]
    .filter(Boolean)
    .join(' ');
  const locality = [address.city, address.state].filter(Boolean).join(', ');
  const area = [locality, address.postalCode].filter(Boolean).join(' ');
  return [street, area].filter(Boolean).join(', ');
}

const CLASSIFY_PATH = '/v1/harmony-chat-proxy/classify';
const AI_TIMEOUT_MS = 5000;

const NORMALIZE_PROMPT = `You normalize a 911 civic address from the United States or Canada.
The input is often a broken CAD line: all caps, missing commas, street-type suffixes (ST, AVE, RD, BLVD, DR, LN, CT, HWY, PKWY, RU, BD), directionals (N, S, E, W), and junk such as ADRESSES MULTIPLES.
Return the most likely real civic address.
Expand US street types and directionals (N MAIN ST -> North Main Street).
For Quebec, put the street type first (RU -> Rue, BD -> Boulevard) and restore accents.
Do not invent a building number. Do not invent a postal code that is not in the input.
If the input is already a normal address, split it and do not rename the street.
country is "US" or "CA" when known, otherwise "".
state and region are the 2-letter state or province code when known.
Reply with JSON only:
{"buildingNumber":"","streetName":"","city":"","region":"","state":"","country":"","postalCode":""}`;

const aiBySource = new Map<string, NormalizedAddress>();
const aiInflight = new Map<string, Promise<NormalizedAddress | null>>();

function sourceKey(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

function recallNormalizedAddress(source: string): NormalizedAddress | null {
  return aiBySource.get(sourceKey(source)) ?? null;
}

/** Text the model should see: the civic line, else the reverse-geocoded line. */
export function normalizationSource(
  location?: {
    address_single_line?: string | null;
    rgAddress?: string | null;
    addressObject?: AddressObject | null;
  } | null,
): string {
  const civic =
    typeof location?.address_single_line === 'string'
      ? location.address_single_line.trim().replace(/\s+/g, ' ')
      : '';
  if (civic) return civic;
  const rg =
    typeof location?.rgAddress === 'string'
      ? location.rgAddress.trim().replace(/\s+/g, ' ')
      : '';
  if (rg) return rg;
  const obj = location?.addressObject;
  if (!obj) return '';
  return [
    obj.home,
    obj.street,
    obj.city,
    obj.region,
    obj.postal_code,
    obj.country,
  ]
    .map(part => (typeof part === 'string' ? part.trim() : ''))
    .filter(Boolean)
    .join(', ');
}

/**
 * Ask the classifier to repair a US or Canadian civic line.
 * Returns null when the model is unavailable or the reply is unusable,
 * so the caller keeps the synchronous parse already on the location.
 * A successful reply is cached and reused by `buildNormalizedAddress`.
 */
export async function normalizeAddressWithAI(
  source: string,
  signal?: AbortSignal,
): Promise<NormalizedAddress | null> {
  const key = sourceKey(source);
  if (!key) return null;
  const cached = aiBySource.get(key);
  if (cached) return cached;
  const pending = aiInflight.get(key);
  if (pending) return pending;

  const request = requestNormalizedAddress(source, signal).finally(() => {
    aiInflight.delete(key);
  });
  aiInflight.set(key, request);
  return request;
}

async function requestNormalizedAddress(
  source: string,
  signal?: AbortSignal,
): Promise<NormalizedAddress | null> {
  let token = '';
  try {
    token = getTokens()?.token || '';
  } catch {
    return null;
  }
  if (!token) return null;

  const timeout =
    typeof AbortSignal !== 'undefined' &&
    typeof AbortSignal.timeout === 'function'
      ? AbortSignal.timeout(AI_TIMEOUT_MS)
      : undefined;
  const requestSignal =
    signal && timeout && typeof AbortSignal.any === 'function'
      ? AbortSignal.any([signal, timeout])
      : signal;

  try {
    const response = await fetch(`${getAPIHost()}${CLASSIFY_PATH}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        messages: [
          { role: 'system', content: NORMALIZE_PROMPT },
          { role: 'user', content: source.trim() },
        ],
        temperature: 0,
        max_tokens: 180,
        json_mode: true,
      }),
      signal: requestSignal,
    });
    if (!response.ok) return null;
    const data = await response.json();
    const parsed = parseModelAddress(
      typeof data?.content === 'string' ? data.content : '',
      source,
    );
    if (!parsed) return null;
    aiBySource.set(sourceKey(source), parsed);
    return parsed;
  } catch {
    return null;
  }
}

function parseModelAddress(
  content: string,
  source: string,
): NormalizedAddress | null {
  let raw: Record<string, unknown>;
  try {
    raw = JSON.parse(content) as Record<string, unknown>;
  } catch {
    return null;
  }
  const address: NormalizedAddress = {
    buildingNumber: asText(raw.buildingNumber),
    streetName: asText(raw.streetName),
    city: asText(raw.city),
    region: shortCode(asText(raw.region)),
    state: shortCode(asText(raw.state)),
    country: normalizeCountry(asText(raw.country)),
    postalCode: asText(raw.postalCode).toUpperCase(),
  };
  if (!address.streetName && !address.city) return null;
  if (address.country !== 'US' && address.country !== 'CA') {
    address.country = '';
  }
  if (!address.state) address.state = address.region;
  if (!address.region) address.region = address.state;

  const sourceNumber = source.trim().match(/^\d+/)?.[0];
  if (sourceNumber && address.buildingNumber) {
    const modelNumber = address.buildingNumber.match(/^\d+/)?.[0];
    if (modelNumber !== sourceNumber) return null;
  }
  if (sourceNumber && !address.buildingNumber) {
    address.buildingNumber = sourceNumber;
  }
  const compactPostal = address.postalCode.replace(/\s+/g, '').toUpperCase();
  const compactSource = source.replace(/\s+/g, '').toUpperCase();
  if (compactPostal && !compactSource.includes(compactPostal)) {
    address.postalCode = '';
  }
  return address;
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function shortCode(value: string): string {
  return value.length > 0 && value.length <= 3 ? value.toUpperCase() : value;
}

export function resetNormalizedAddressCache(): void {
  aiBySource.clear();
  aiInflight.clear();
}

function parseAddress(
  line: string,
  fallbackCity?: string | null,
): NormalizedAddress {
  const parts = line
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  const streetPart = parts[0] || '';
  let city = '';
  let regionSource = '';

  if (parts.length >= 3) {
    city = parts[1];
    regionSource = parts.slice(2).join(' ');
  } else if (parts.length === 2) {
    const split = splitTrailingRegion(parts[1]);
    city = split.city;
    regionSource = split.region;
  }

  if (!city) city = clean(fallbackCity);

  const civic = streetPart.match(CIVIC_NUMBER);
  const buildingNumber = civic?.[1] || '';
  const streetName = civic?.[2] || streetPart;
  const { region, postalCode } = splitRegion(regionSource);

  return {
    buildingNumber,
    streetName,
    city,
    region,
    state: region,
    country: countryForRegion(region),
    postalCode,
  };
}

/**
 * Returns a repaired line when the street type sits at the end in
 * all caps. Returns null when the line should be kept as written.
 */
function repairBrokenCadLine(line: string): string | null {
  const parts = line
    .split(',')
    .map(part => part.trim())
    .filter(Boolean);
  const street = parts[0];
  if (!street || !isAllCaps(street)) return null;

  const civic = street.match(CIVIC_NUMBER);
  if (!civic) return null;

  const { city, region } = readCityAndRegion(parts.slice(1));
  const nameTokens = civic[2].split(/\s+/).filter(Boolean);
  const typeKey = nameTokens[nameTokens.length - 1]
    ?.replace(/\./g, '')
    .toUpperCase();
  const typeLabel = streetTypeLabel(typeKey, city, region);
  if (!typeLabel) return null;

  const streetName = formatFrenchName(nameTokens.slice(0, -1));
  const formattedStreet = [civic[1], typeLabel, streetName]
    .filter(Boolean)
    .join(' ');
  const formattedCity = formatPlaceName(city);
  const { region: regionCode, postalCode } = splitRegion(region);
  const formattedRegion = [regionCode, postalCode].filter(Boolean).join(' ');

  return [formattedStreet, formattedCity, formattedRegion]
    .filter(Boolean)
    .join(', ');
}

function readCityAndRegion(parts: string[]): { city: string; region: string } {
  if (parts.length >= 2) {
    return { city: parts[0], region: parts.slice(1).join(' ') };
  }
  if (parts.length === 1) return splitTrailingRegion(parts[0]);
  return { city: '', region: '' };
}

function splitTrailingRegion(segment: string): {
  city: string;
  region: string;
} {
  const { withoutPostal, postal } = peelPostal(segment);
  const tokens = withoutPostal.split(/\s+/).filter(Boolean);
  if (tokens.length < 2) {
    return { city: withoutPostal, region: postal };
  }
  const last = tokens[tokens.length - 1];
  const code = provinceCode(last);
  if (!code && !US_STATES.has(last.replace(/\./g, '').toUpperCase())) {
    return { city: withoutPostal, region: postal };
  }
  return {
    city: tokens.slice(0, -1).join(' '),
    region: [last, postal].filter(Boolean).join(' '),
  };
}

function splitRegion(region: string): { region: string; postalCode: string } {
  const { withoutPostal, postal } = peelPostal(region);
  const token = withoutPostal.split(/\s+/).filter(Boolean).pop() || '';
  const code = provinceCode(token) || usStateCode(token);
  return { region: code, postalCode: postal };
}

function provinceCode(token: string): string {
  return PROVINCE_CODES[token.replace(/\./g, '').toUpperCase()] || '';
}

function usStateCode(token: string): string {
  const key = token.replace(/\./g, '').toUpperCase();
  return US_STATES.has(key) ? key : '';
}

function countryForRegion(region: string): string {
  if (CA_PROVINCES.has(region)) return 'CA';
  if (US_STATES.has(region)) return 'US';
  return '';
}

function streetTypeLabel(
  token: string | undefined,
  city: string,
  region: string,
): string | undefined {
  if (!token) return undefined;
  if (FRENCH_STREET_TYPES[token]) return FRENCH_STREET_TYPES[token];
  if (isQuebec(city, region)) return QUEBEC_STREET_TYPES[token];
  return undefined;
}

function isQuebec(city: string, region: string): boolean {
  if (provinceCode(region.split(/\s+/)[0] || '') === 'QC') return true;
  return city.replace(/\./g, '').toUpperCase() === 'QUEBEC';
}

function formatFrenchName(tokens: string[]): string {
  const words: string[] = [];
  tokens.forEach(token => {
    const key = token.replace(/\./g, '').toUpperCase();
    if (words.length > 0 && ROMAN.has(key)) {
      words[words.length - 1] = `${words[words.length - 1]}-${key}`;
      return;
    }
    words.push(formatFrenchToken(token));
  });
  return words.join(' ');
}

function formatFrenchToken(token: string): string {
  const pieces = token.split("'");
  if (pieces.length === 2) {
    const [left, right] = pieces;
    const leftLower = left.toLowerCase();
    const rightText = capitalize(restoreAccents(right.toLowerCase()));
    if (leftLower === 'l' || leftLower === 'd')
      return `${leftLower}'${rightText}`;
  }
  const key = token.replace(/\./g, '').toUpperCase();
  if (ROMAN.has(key)) return key;
  const lower = restoreAccents(token.toLowerCase());
  if (PARTICLES.has(lower)) return lower;
  return capitalize(lower);
}

function formatPlaceName(city: string): string {
  if (!city || !isAllCaps(city)) return city;
  return city
    .split('-')
    .map(part =>
      part
        .split(/\s+/)
        .map(token => capitalize(restoreAccents(token.toLowerCase())))
        .join(' '),
    )
    .join('-');
}

function restoreAccents(lower: string): string {
  if (lower === 'quebec') return 'québec';
  if (lower === 'montreal') return 'montréal';
  if (lower.endsWith('iere')) return `${lower.slice(0, -4)}ière`;
  return lower;
}

function peelPostal(value: string): { withoutPostal: string; postal: string } {
  const match = value.match(POSTAL);
  if (!match || match.index == null) {
    return { withoutPostal: value.trim(), postal: '' };
  }
  const withoutPostal =
    `${value.slice(0, match.index)}${value.slice(match.index + match[0].length)}`.trim();
  const compact = match[0].replace(/\s+/g, '').toUpperCase();
  return {
    withoutPostal,
    postal: `${compact.slice(0, 3)} ${compact.slice(3)}`,
  };
}

function capitalize(word: string): string {
  if (!word) return word;
  return word.charAt(0).toLocaleUpperCase('fr-CA') + word.slice(1);
}

function isAllCaps(value: string): boolean {
  const letters = value.replace(/[^A-Za-zÀ-ÿ]/g, '');
  return letters.length > 0 && letters === letters.toUpperCase();
}

function clean(value?: string | null): string {
  return typeof value === 'string' ? value.trim() : '';
}
